import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Target, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <SEO 
        title="About Legend - Revolutionizing Digital Comics with Audio"
        description="Learn about Legend's mission to revolutionize digital comics. We're bringing a new era of interactive and engaging audiovisual storytelling to readers worldwide."
        keywords="about Legend, digital comics company, audiovisual comics, comic innovation, Zing Technologies"
        url="https://visual-story-forge.emergent.host/about"
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/60 backdrop-blur-xl border-b border-[#FFB966]/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group cursor-pointer">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#FFB966] to-[#FF8C42] blur-md opacity-50 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative flex gap-0.5">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="w-1.5 h-10 bg-gradient-to-b from-[#FFB966] to-[#FF8C42] rounded-full transform rotate-12" style={{ marginLeft: i > 0 ? '2px' : '0' }}></div>
                  ))}
                </div>
              </div>
              <span className="text-2xl font-bold tracking-wider bg-gradient-to-r from-[#FFB966] to-[#FFC57D] bg-clip-text text-transparent">LEGEND</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-sm font-medium hover:text-[#FFB966] transition-colors">Home</Link>
              <Link to="/partners" className="text-sm font-medium hover:text-[#FFB966] transition-colors">Partners</Link>
              <Link to="/about" className="text-sm font-medium text-[#FFB966]">About Us</Link>
              <Link to="/contact" className="text-sm font-medium hover:text-[#FFB966] transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            We Love a <span className="bg-gradient-to-r from-[#FFB966] to-[#FFC57D] bg-clip-text text-transparent">Good Story!</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            At Legend, we believe that comics have the power to transport us to extraordinary worlds and ignite our imaginations.
          </p>
        </div>
      </section>

      {/* Vision */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <Card className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-[#FFB966]/20">
            <CardContent className="p-12">
              <p className="text-xl text-gray-300 leading-relaxed">
                We're dedicated to revolutionizing the way stories are told and experienced. Our vision extends beyond just providing exceptional content. We strive to push the boundaries of what's possible in digital comics, setting new standards for the industry. Through innovation, unbounded storytelling, and a commitment to quality, we are determined to shape the future of comics.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 px-6 bg-gradient-to-b from-transparent via-black/40 to-transparent">
        <div className="max-w-5xl mx-auto text-center">
          <div className="w-20 h-20 mx-auto mb-8 rounded-2xl bg-gradient-to-br from-[#FFB966]/20 to-[#FF8C42]/20 flex items-center justify-center">
            <Target className="w-10 h-10 text-[#FFB966]" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Our <span className="bg-gradient-to-r from-[#FFB966] to-[#FFC57D] bg-clip-text text-transparent">Mission</span>
          </h2>
          <p className="text-2xl text-gray-300">
            Our mission at Legend is to bring a new era of interactive and engaging digital comics to readers worldwide.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-[#FFB966]/20 p-12 md:p-16 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-[#FFB966]/10 via-transparent to-[#FF8C42]/10"></div>
            
            <div className="relative z-10 space-y-8">
              <h2 className="text-4xl md:text-5xl font-bold">
                Ready to Experience Comics Like <span className="bg-gradient-to-r from-[#FFB966] to-[#FFC57D] bg-clip-text text-transparent">Never Before?</span>
              </h2>
              
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Download the Legend app today and dive into a world of audiovisual storytelling.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
                <a href="https://apps.apple.com/us/app/legendhq-audiovisual-comics/id6444578827" target="_blank" rel="noopener noreferrer">
                  <img 
                    src="https://cdn.prod.website-files.com/6357a366954c9c7fad43fdd8/64fdb56d5a16efb4befdd9bb_Download%20on%20App%20Store.png" 
                    alt="Download on App Store" 
                    className="h-14 hover:scale-105 transition-transform cursor-pointer"
                  />
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.legendhq.mobile.android.legendapp" target="_blank" rel="noopener noreferrer">
                  <img 
                    src="https://cdn.prod.website-files.com/6357a366954c9c7fad43fdd8/64fdb5623ba4d520ddbde2aa_Download%20on%20Google%20Play.png" 
                    alt="Get it on Google Play" 
                    className="h-14 hover:scale-105 transition-transform cursor-pointer"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-gray-500 text-sm">
              © 2026 Zing Technologies Inc. All rights reserved.
            </div>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-gray-400 hover:text-[#FFB966] transition-colors">Privacy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-[#FFB966] transition-colors">Terms</Link>
              <Link to="/contact" className="text-gray-400 hover:text-[#FFB966] transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;